import { getRequiredSlots, getSlotSchema } from '../gphSkillRegistry';
import { normalizePubgCalculatorSlotAliases } from '../slotFilling/pubgCalculatorSlotExtractor';

const SUPPORTED_PUBG_SCOPES = new Set(['red_dot', 'holo', '2x', '3x', '4x', '6x', '8x']);

const normalizeSensitivityType = (value) => {
  if (Array.isArray(value)) return value;
  const normalized = String(value || '').trim().toLowerCase();
  if (['ads gyro', 'adsgyro', 'gyro ads', 'giroscopio ads', 'ads giroscopio'].includes(normalized)) return 'ADS Gyro';
  if (normalized === 'ads') return 'ADS';
  if (['gyro', 'giroscopio', 'giroscópio'].includes(normalized)) return 'Gyro';
  if (['camera', 'câmera'].includes(normalized)) return 'Camera';
  return value;
};

const hasAmbiguousSensitivityModes = (value) => {
  if (Array.isArray(value)) return value.length > 1;
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return false;
  if (['ads gyro', 'adsgyro', 'gyro ads', 'giroscopio ads', 'ads giroscopio'].includes(normalized)) return false;
  const hits = [
    /\bads\b/.test(normalized),
    /\bgyro\b|\bgiroscopio\b|\bgirosc.pio\b/.test(normalized),
    /\bcamera\b|\bc.mera\b/.test(normalized)
  ].filter(Boolean).length;
  return hits > 1;
};

export const validatePubgCalculatorSlots = (slots = {}, extractionIssues = []) => {
  const rawSensitivityType = slots.sensitivityType ?? slots.controlMode;
  const rawAmbiguousSensitivityModes = hasAmbiguousSensitivityModes(rawSensitivityType);
  const normalizedSlots = normalizePubgCalculatorSlotAliases(slots);
  if (normalizedSlots.sensitivityType) {
    normalizedSlots.sensitivityType = normalizeSensitivityType(normalizedSlots.sensitivityType);
  }

  const missingSlots = getRequiredSlots('pubg_mobile_calculator').filter((slotName) => (
    normalizedSlots[slotName] === undefined
    || normalizedSlots[slotName] === null
    || normalizedSlots[slotName] === ''
  ));

  const invalidSlots = [...(Array.isArray(extractionIssues) ? extractionIssues : [])];

  if (normalizedSlots.game && normalizedSlots.game !== 'pubg_mobile') {
    invalidSlots.push({
      slot: 'game',
      value: normalizedSlots.game,
      reason: normalizedSlots.game === 'codm' ? 'codm_calculator_unavailable' : 'unsupported_game'
    });
  }

  if (normalizedSlots.baseValue !== undefined) {
    const numericValue = Number(normalizedSlots.baseValue);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      invalidSlots.push({
        slot: 'baseValue',
        value: normalizedSlots.baseValue,
        reason: 'numeric_value_required'
      });
    } else {
      normalizedSlots.baseValue = numericValue;
    }
  }

  if (
    normalizedSlots.baseScope
    && !SUPPORTED_PUBG_SCOPES.has(normalizedSlots.baseScope)
    && !invalidSlots.some((issue) => issue.slot === 'baseScope' && issue.reason === 'unsupported_scope')
  ) {
    invalidSlots.push({
      slot: 'baseScope',
      value: normalizedSlots.baseScope,
      reason: 'unsupported_scope'
    });
  }

  if (normalizedSlots.sensitivityType) {
    const schema = getSlotSchema('pubg_mobile_calculator', 'sensitivityType');
    if (rawAmbiguousSensitivityModes || hasAmbiguousSensitivityModes(normalizedSlots.sensitivityType)) {
      invalidSlots.push({
        slot: 'sensitivityType',
        value: normalizedSlots.sensitivityType,
        reason: 'ambiguous_sensitivity_type'
      });
    } else if (!schema?.values?.includes(normalizedSlots.sensitivityType)) {
      invalidSlots.push({
        slot: 'sensitivityType',
        value: normalizedSlots.sensitivityType,
        reason: 'unsupported_sensitivity_type'
      });
    }
  }

  return {
    valid: missingSlots.length === 0 && invalidSlots.length === 0,
    normalizedSlots,
    missingSlots,
    invalidSlots
  };
};

const formatScopeLabel = (slots = {}) => {
  if (slots.baseScopeLabel) return slots.baseScopeLabel;
  if (slots.baseScope === 'red_dot') return 'Red Dot';
  return slots.baseScope || 'mira';
};

const formatWeaponLabel = (slots = {}) => (
  slots.weaponLabel || (slots.weapon === 'm762' ? 'Beryl M762' : slots.weapon || 'arma')
);

export const buildMissingPubgSlotsMessage = (missingSlots = [], slots = {}) => {
  const missing = new Set(missingSlots);
  const parts = [];

  if (missing.has('baseScope')) parts.push('qual mira voce ja tem como base');
  if (missing.has('baseValue')) parts.push('qual valor numerico esta bom nela');
  if (missing.has('sensitivityType')) parts.push('qual tipo de sensibilidade: Camera, ADS, Gyro ou ADS Gyro');
  if (missing.has('weapon')) parts.unshift('qual arma voce quer calcular');

  if (parts.length === 0) return 'Preciso de mais um dado para calcular com seguranca.';
  if (!missing.has('weapon') && !missing.has('baseScope') && (missing.has('baseValue') || missing.has('sensitivityType'))) {
    return `Entendi. Voce quer usar sua ${formatScopeLabel(slots)} da ${formatWeaponLabel(slots)} como base para calcular as outras miras. Me diga ${parts.join(' e ')}.`;
  }
  return `Para calcular sem chute, me diga ${parts.join(', ')}.`;
};

export const buildInvalidPubgSlotsMessage = (invalidSlots = []) => {
  const first = invalidSlots[0];
  if (!first) return null;

  if (first.reason === 'codm_calculator_unavailable') {
    return 'Calculadora automatica de CODM ainda nao esta disponivel. Posso te ajudar com Sensi-IA ou treino para CODM.';
  }

  if (first.reason === 'unsupported_scope') {
    return `Nao reconheci a mira ${first.value}. Me diga uma mira valida, como Red Dot, 2x, 3x, 4x, 6x ou 8x.`;
  }

  if (first.reason === 'numeric_value_required') {
    return 'Preciso do valor em numero. Exemplo: 115.';
  }

  if (first.reason === 'ambiguous_sensitivity_type') {
    return 'Nao vou misturar modos de sensibilidade. Escolha um por vez: Camera, ADS, Gyro ou ADS Gyro.';
  }

  if (first.reason === 'unsupported_sensitivity_type') {
    return 'Esse tipo de sensibilidade nao esta claro. Use Camera, ADS, Gyro ou ADS Gyro.';
  }

  return 'Nao consegui validar esses dados. Me manda a arma, mira base, valor numerico e tipo de sensibilidade.';
};

export default validatePubgCalculatorSlots;
